/**
 * DEC-154 rule-sync — reactive debounced push, A-path engine-native item
 * kinds (Sequencer arm, Macro arm, LFO start, Button fire/flip) PLUS the one
 * non-item binding, Panic (see `panicRule` below). Same shape as
 * `autotune/docSync.ts` (removed 2026-09-28): a debounced effect over the projects signal + a
 * flush on (re)connect, never throws.
 *
 * Two things are kept in sync, not one — both load-bearing for the "fires
 * even with the tab closed" promise DEC-154 makes:
 *
 * 1. **The rule itself** — `POST /rules/opbuttons` replaces the WHOLE rule
 *    set (no per-rule PUT exists on OPBridge — see `httpClient.ts`'s
 *    `putRuleSet` doc comment), one `Rule` per bound item.
 * 2. **The bound item's own library entry, where one exists** — Sequencer/
 *    Macro's rule action is `{module:"sequencer"/"macro", command:"arm",
 *    args:{...Id}}`, which 404s if that id isn't already in OPBridge's
 *    engine library. Today the ONLY thing that pushes a sequencer/macro
 *    there is a manual PLAY/arm tap in the PWA — so a binding on something
 *    never once manually played this session would silently fail to arm at
 *    a gig with the tab closed, defeating the entire point of A-path.
 *    Closed here by reusing the existing `pushSequencer()`/`pushMacro()`
 *    (dispatch.ts) for every bound item on the same debounced trigger, so a
 *    binding alone is enough — no manual-play prerequisite. LFO has NO
 *    separate library (confirmed against OPBridge's own
 *    `rules_wiring.rs::build_lfo_params` — the rule's `args` carries the
 *    full resolved params directly, same one-step shape as a static
 *    MIDI/OSC action), so `pushLibraryEntry` is a no-op for it.
 *
 * Live-tracking, not a frozen Learn-time snapshot: every field an action
 * needs (`item.sequencerId`, `item.ccNumber`, the connected bridge's
 * `defaultMidiOutput`, …) is re-read fresh on every debounced sync, not
 * captured once when the binding was learned. This is deliberate — these
 * are all PERSISTED item/connection fields the operator can keep editing
 * after learning a binding (re-point which sequencer an item plays, tweak
 * an LFO's range), and the pedal should track "whatever this item is
 * configured to do right now," the same way a physical footswitch wired to
 * a physical console button would. This is NOT the same case DEC-154's
 * "capture the RESOLVED step, not the button's intent" contract addresses —
 * that's about freezing genuinely-ephemeral RUNTIME intent (a toggle
 * button's current lit-state) into a byte value at Learn time, because
 * there is no persisted field to re-read later. Every field used below is a
 * persisted field.
 *
 * Deliberately a plain switch per kind, not a generic lookup table — this
 * codebase's other per-kind dispatch (e.g. `layout/store.ts`'s `ItemPatch`
 * union) favors explicit enumeration over generic cleverness, and each
 * kind's action shape is genuinely different (id-reference vs. embedded
 * resolved params).
 */

import { createEffect, on } from 'solid-js';
import {
  projects, activeProject, allMidiTriggerItems, activePedals, PANIC_TRIGGER_ID, buttonEmitsMidi, projectUsesMotionPresets,
  type LayoutItem, type SequencerItem, type MacroItem, type LfoItem, type ButtonItem,
  type PresetChangeItem, type SnapshotChangeItem, type MidiTriggerBinding,
} from '../layout/projectStore.ts';
import { resolveFireableItemActions } from '../timeline/bridgeSync.ts';
import { pedalToggleViaMesh } from './pedalMesh.ts';
import { MOTION_STATE_ADDRESS, MOTION_STATE_COMMAND } from '../timeline/opMotion.ts';
import { PANIC_PWA_COMMAND } from '../performance/panic.ts';
import { sequencers } from '../sequencer/store.ts';
import { macros } from '../macros/store.ts';
import { bridges, getConnection } from '../opbridge/store.ts';
import type { BridgeConfig } from '../opbridge/storage.ts';
import { pushSequencer, pushMacro, pushToggle, toggleActions, type ToggleActionConfig } from '../opbridge/dispatch.ts';
import type { Rule, RuleAction, RuleCondition, RuleSet } from '../opbridge/httpClient.ts';

const CONSUMER = 'opbuttons';
const DEBOUNCE_MS = 1500;

/** Item kinds DEC-154 currently supports as A-path (OPBridge-native rule
 *  actions exist for these already). Extend as more kinds generalize.
 *
 *  Button joined 2026-08-28. It is the kind DEC-154 named FIRST and shipped
 *  LAST, and the reason is worth recording so the next kind is scoped right:
 *  Sequencer/Macro/LFO all resolve to an action carrying an ID (or resolved
 *  engine params) that OPBridge looks up, whereas a Button resolves to raw
 *  BYTES. That put it in the deferred "resolve raw bytes at Learn time"
 *  group, which was tracked as transpose/capo/Waves/grids and silently did
 *  not name the button - so the most basic case stayed invisible in the plan
 *  for three weeks while the exotic ones were listed. */
type EngineItem = SequencerItem | MacroItem | LfoItem | ButtonItem
  | PresetChangeItem | SnapshotChangeItem;

/** A Button whose action cannot be expressed as an OPBridge rule today.
 *
 *  TWO exclusions, both verified against the live daemon rather than assumed:
 *
 *  1. **`actionType: 'ableton'`** - that chain can carry `clock: 'practice'`
 *     steps, which dispatch intercepts BEFORE any bridge call and resolves
 *     against a browser-local signal. A rule would fire the OSC steps and
 *     silently skip the practice-clock ones, and a binding that fires PART of
 *     a chain at a gig is worse than no binding.
 *
 *  Toggle mode was ALSO excluded here for a few hours on 2026-08-28 and is
 *  now back: `module: "toggle"` was missing from `opbridge-rules::arm::
 *  KNOWN_MODULES`, so arming returned `unknown module 'toggle'` - and because
 *  `arm_rule_set` propagates with `?`, ONE such rule rejected the ENTIRE set,
 *  meaning a single toggle-bound button would have silently disarmed every
 *  other binding including Panic. Reported and fixed the same day (OPBridge
 *  S242, `v1.15.92+6e8dd4f`); their dispatcher had routed `"toggle"` to
 *  `flip_toggle_and_fire` the whole time, only the arm-time validator never
 *  learned the word. Re-verified live here before re-enabling, not taken on
 *  the changelog's word.
 *
 *  The exclusion is mirrored in the Inspector so the operator sees the limit
 *  at author time instead of discovering a dead binding at a gig. */
function buttonIsExpressible(item: ButtonItem): boolean {
  return (item.actionType ?? 'cc') !== 'ableton';
}

function isEngineItem(item: LayoutItem): item is EngineItem {
  if (item.kind === 'button') return buttonIsExpressible(item);
  return item.kind === 'sequencer' || item.kind === 'macro' || item.kind === 'lfo'
    || item.kind === 'presetChange' || item.kind === 'snapshotChange';
}

/** A Button's wire config, resolved LIVE exactly the way `FireButton.tsx`
 *  resolves it for a finger tap - a bound Guitar FX pedal overrides both
 *  port and channel, an ordinary button uses its own fields plus the
 *  bridge's default output. Returns null when the port cannot be resolved
 *  (no bridge default and no pedal), matching how LFO refuses rather than
 *  pushing a rule with an empty port.
 *
 *  A `pedalId` that no longer resolves returns null too: DEC-124/138 makes a
 *  dangling pedal a loud dead state, and falling back to the item's stale
 *  channel would send a gig-time message to the wrong device. */
export function buttonWire(item: ButtonItem, cfg: BridgeConfig): { port: string; channel: number } | null {
  if (item.pedalId) {
    const pedal = activePedals().find((pd) => pd.id === item.pedalId);
    if (!pedal?.port) return null;
    return { port: pedal.port, channel: pedal.channel ?? item.channel };
  }
  if (!cfg.defaultMidiOutput) return null;
  return { port: cfg.defaultMidiOutput, channel: item.channel };
}

/** Mirrors `FireButton.tsx`'s `effectiveMode()` - pc and anything that emits
 *  no MIDI collapse to trigger regardless of the stored `mode`, because none
 *  of them has an OFF message to pair with. Kept in lockstep with that
 *  function by name so a change to one is findable from the other.
 *
 *  ⚠️ This used to name `pc || ableton` and fall through for everything else,
 *  which meant an `obs` button reached here as a TOGGLE and
 *  {@link buttonToggleConfig} then handed it a cc-0 wire - the inbound-MIDI
 *  twin of the timeline bug fixed in `bridgeSync.ts` on 2026-09-19, in a path
 *  that fix did not cover. Latent rather than live (the Inspector hides Learn
 *  on an OBS button, so no binding can currently be authored), but it was one
 *  new action type away from being real. Asked via `buttonEmitsMidi` now, so
 *  the answer cannot rot. */
function buttonMode(item: ButtonItem): 'toggle' | 'momentary' | 'trigger' {
  const action = item.actionType ?? 'cc';
  if (action === 'pc' || !buttonEmitsMidi(action)) return 'trigger';
  return item.mode ?? 'toggle';
}

/** The MIDI toggle wire for a button, or null when it has none.
 *
 *  Null for `pc` (no OFF counterpart) and for every action type that emits no
 *  MIDI at all - see {@link buttonMode}'s warning for what fell through here
 *  before, and `buttonEmitsMidi` for why the question is asked this way
 *  round. A null lands on `actionsFor`'s `if (!toggleCfg) return []`, so such
 *  a button contributes no rule action rather than a wrong one. */
export function buttonToggleConfig(item: ButtonItem, channel: number): ToggleActionConfig | null {
  const action = item.actionType ?? 'cc';
  if (action === 'pc' || !buttonEmitsMidi(action)) return null;
  return action === 'noteon'
    ? { actionType: 'noteon', channel, note: item.note ?? 60, velocity: item.velocity ?? 127 }
    // offValue too: FireButton's own push carries it and both write the SAME
    // `/toggles/opbuttons/{id}`, so leaving it out reset a custom off value
    // to 0 whenever a rule or a mod share re-pushed the toggle.
    : { actionType: 'cc', channel, ccNumber: item.ccNumber, onValue: item.onValue, offValue: item.offValue ?? 0 };
}

let timer: number | null = null;
let lastPushedJson: string | null = null;

/** The primary bridge if it exists and is connected (DEC-063: primary
 *  only) — same helper as every other reactive-push module. */
function pickPrimaryConnected(): BridgeConfig | null {
  const all = bridges();
  if (all.length === 0) return null;
  const primary = all.find((cfg) => cfg.is_primary) ?? all[0]!;
  const conn = getConnection(primary.id);
  return conn && conn.status() === 'connected' ? primary : null;
}

function boundEngineItems(): EngineItem[] {
  return allMidiTriggerItems().filter(isEngineItem);
}

function clamp01(n: number): number {
  return Math.max(0, Math.min(1, n));
}

/** Builds the rule's fire-side action. Returns `null` when the item can't
 *  currently resolve to a valid action (e.g. an LFO with no bridge default
 *  MIDI output configured) — the caller drops the rule entirely rather than
 *  push one with a broken/empty field, mirroring how `armLfoOnBridge`
 *  refuses to arm ("port-not-configured") instead of sending garbage.
 *
 *  LFO's `args` shape is byte-verified against OPBridge's own
 *  `rules_wiring.rs::build_lfo_params` + its unit tests (not guessed, not
 *  taken from the PWA-side `ArmLfoParams`/HTTP-body shapes, which are BOTH
 *  differently-shaped): flat `lfoId`/`port`/`channel`/`ccNumber` (not
 *  nested under `target`), `amplitude`/`center` normalized 0..1 (not raw
 *  MIDI 0..127 the way `ArmLfoParams` carries them — same `/127` clamp
 *  `armLfoOnBridge` already applies for the HTTP path, reused here so a
 *  learned trigger and a manual PLAY tap always agree numerically).
 *  `loop: true` is hardcoded — `LfoItem` has no persisted loop field
 *  (`SineLfo.tsx`'s loop toggle is ephemeral component-local UI state,
 *  defaulting true on every mount), so there is nothing to read; true
 *  matches that same default. v1 scope is "start" only, matching Sequencer/
 *  Macro's single-action shape — no stop-binding UI yet (a second binding
 *  type, out of scope for this slice). */
/**
 * Returns the rule's action LIST, not a single action - a trigger-mode
 * Button is genuinely two sends.
 *
 * ─── Why a Button is two actions and the others are one ───
 *
 * `FireButton.tsx`'s trigger branch fires `sendOnce(true)` then
 * `sendOnce(false)` in the same tick (a blip, which is what scene-launch
 * wants). Emitting only the ON half here would leave a note-mode button
 * SUSTAINING after a pedal stomp, which that file calls out as "the worst
 * possible failure mode for a live performer". So trigger emits both, in
 * order, and a pedal press produces the identical wire to a finger tap.
 *
 * ✅ **Momentary genuinely HOLDS as of 2026-08-28.** It shipped hours earlier
 * degrading to trigger, on the belief that no release rule existed - true of
 * THIS file, false of the engine. `opbridge-rules` has had a `NoteOff` leaf all
 * along, and `engine.rs` matches `NoteEquals`/`ChannelEquals` against
 * `Event::MidiNoteOff`, so the release edge was expressible with no OPBridge
 * work. Checked against their source before building rather than inferred from
 * this file's own prior comment - which is exactly the mistake that made
 * "there is NO release handling in the inbound-MIDI path" read as a platform
 * limit when it was a local one.
 *
 * `rulesFor` now emits TWO rules for a momentary Button and splits these two
 * actions across the edges: press fires `[0]`, release fires `[1]`. The pair is
 * still returned as one array here because trigger mode genuinely wants both on
 * the press edge (a blip); only the momentary branch separates them.
 */
function actionsFor(item: EngineItem, cfg: BridgeConfig): RuleAction[] {
  switch (item.kind) {
    case 'sequencer':
      return [{ module: 'sequencer', command: 'arm', args: { sequencerId: item.sequencerId } }];
    case 'macro':
      return [{ module: 'macro', command: 'arm', args: { macroId: item.macroId } }];
    // Helix preset + snapshot delegate to the Timeline's resolver rather than
    // re-deriving the wire. A PresetChange is a THREE-message compound send
    // (CC32 setlist, then Program Change, then optionally CC69 snapshot) whose
    // ORDER matters, and duplicating that here is exactly how a pedal press
    // and a timeline cue would drift apart. `Rule.actions` preserves array
    // order, and OPBridge fires same-bar cue actions in authored order, so the
    // one resolver satisfies both consumers.
    case 'presetChange':
    case 'snapshotChange': {
      const resolved = resolveFireableItemActions(item, cfg);
      // null = no pedal bound, pedal removed (DEC-138 dead state), or this
      // bridge is not one of the item's targets. Dropping the rule is right in
      // all three - the same refusal `armLfoOnBridge` makes for a missing port.
      return resolved ? resolved.on : [];
    }
    case 'button': {
      // A toggle whose taps go to other computers fires its named action, so a
      // stomp lands where a tap does (audit A-11, `pedalMesh.ts`).
      if (buttonMode(item) === 'toggle' && pedalToggleViaMesh(item, cfg)) {
        return [{ module: 'action', command: 'fire', args: { id: item.id } }];
      }
      const wire = buttonWire(item, cfg);
      if (!wire) return [];
      // Toggle mode delegates to the toggle this button ALREADY maintains in
      // OPBridge's library under its own item id (`FireButton.tsx`'s debounced
      // push, mirrored by `pushLibraryEntry` below so a button on a
      // never-visited page still has one). Flipping the shared toggle rather
      // than sending bytes directly is what keeps a pedal flip and a finger
      // tap on ONE server-side state instead of two that drift apart -
      // OPBridge routes this to the same `flip_toggle_and_fire` that
      // `POST /toggles/{consumer}/flip` uses.
      if (buttonMode(item) === 'toggle') {
        return [{ module: 'toggle', command: 'flip', args: { toggleId: item.id } }];
      }
      // Program Change: one two-byte message, no OFF counterpart (matching
      // `sendOnce`'s pc branch, which returns [] for the release).
      if ((item.actionType ?? 'cc') === 'pc') {
        return [{
          module: 'midi',
          command: 'sendRaw',
          args: { port: wire.port, bytes: [0xc0 | (wire.channel & 0x0f), (item.program ?? 0) & 0x7f] },
        }];
      }
      const toggleCfg = buttonToggleConfig(item, wire.channel);
      if (!toggleCfg) return [];
      const { onAction, offAction } = toggleActions(toggleCfg, wire.port);
      return [onAction, offAction];
    }
    case 'lfo': {
      if (!cfg.defaultMidiOutput) return [];
      return [{
        module: 'lfo',
        command: 'start',
        args: {
          lfoId: item.id,
          port: cfg.defaultMidiOutput,
          channel: item.channel & 0x0f,
          ccNumber: item.ccNumber & 0x7f,
          periodBeats: item.bars * 4,
          amplitude: clamp01((Math.max(item.min, item.max) - Math.min(item.min, item.max)) / 2 / 127),
          center: clamp01((item.min + item.max) / 2 / 127),
          waveform: item.waveform,
          loop: true,
        },
      }];
    }
  }
}

/** Push the bound item's own referenced doc (sequencer/macro definition,
 *  or a toggle-mode Button's toggle) to OPBridge's engine library. Never
 *  throws - the `push*` helpers already swallow their own errors
 *  (dispatch.ts's never-throws contract); this just fans out to the right
 *  one per kind. No-op for LFO - it has no separate library entry (see
 *  module doc comment).
 *
 *  The Button case exists for the same reason the Sequencer/Macro ones do,
 *  and it is NOT redundant with `FireButton.tsx`'s own debounced push: that
 *  effect only runs while the component is MOUNTED, i.e. while the button's
 *  page is the visible one. `allMidiTriggerItems()` deliberately reaches
 *  across every page, so a bound button sitting on a page the operator has
 *  not opened this session would have no toggle in the library and its rule
 *  would 404 at the exact moment a pedal was stomped - the tab-closed gig
 *  case A-path exists to survive. Pushing here makes the binding alone
 *  sufficient, with no "visit the page first" prerequisite.
 *
 *  ⚠️ Only for toggle-mode buttons. Pushing a toggle for a trigger-mode
 *  button would be worse than useless: `PUT` is a FULL REPLACE that resets
 *  `state` to false (see `pushToggle`'s doc comment), and a trigger button's
 *  rule sends bytes directly and never consults the toggle at all. */
async function pushLibraryEntry(item: EngineItem): Promise<void> {
  switch (item.kind) {
    case 'sequencer': {
      const seq = sequencers().find((s) => s.id === item.sequencerId);
      if (!seq) return; // dangling reference — nothing to push, rule 404s until the binding is fixed
      await pushSequencer(seq);
      return;
    }
    case 'macro': {
      const macro = macros().find((m) => m.id === item.macroId);
      if (!macro) return;
      await pushMacro(macro);
      return;
    }
    case 'button': {
      // Only toggle-mode needs a library entry - its rule REFERENCES a toggle
      // by id, so the id must resolve or the flip 404s. Trigger/momentary/pc
      // rules carry their bytes inline and need nothing pushed.
      //
      // NOT redundant with `FireButton.tsx`'s own debounced push: that effect
      // only runs while the component is MOUNTED, i.e. while the button's page
      // is the visible one. `allMidiTriggerItems()` reaches across every page,
      // so a bound button on a page the operator never opened this session
      // would otherwise have no toggle in the library and would 404 at the
      // exact moment a pedal was stomped - the tab-closed gig case A-path
      // exists to survive.
      if (buttonMode(item) !== 'toggle') return;
      const cfg = pickPrimaryConnected();
      if (!cfg) return;
      // Fired through its named action on its own computers (A-11): the share
      // sync keeps THOSE toggles; one here would be a stray copy.
      if (pedalToggleViaMesh(item, cfg)) return;
      const wire = buttonWire(item, cfg);
      if (!wire) return;
      const toggleCfg = buttonToggleConfig(item, wire.channel);
      if (!toggleCfg) return;
      await pushToggle(item.id, item.name, toggleCfg);
      return;
    }
    case 'lfo':
    case 'presetChange':
    case 'snapshotChange':
      // Nothing to push: these carry their bytes inline rather than
      // referencing an OPBridge library entry.
      return;
  }
}

/** Build the rule's match condition from a learned binding.
 *
 *  **The CC branch excludes value 0, and that is a bug fix, not a nicety
 *  (2026-08-23).** A bare `{op:'ccEquals', number}` matches ANY value —
 *  OPBridge's own `LeafPredicate::CcEquals` doc says so explicitly
 *  (`value = None` matches any value) and `engine.rs` implements it as
 *  `n == number && expected.is_none_or(...)`. A footswitch reports its
 *  press as a non-zero value and its release as 0, so the bare form
 *  matched BOTH EDGES and fired the rule twice per stomp. For LFO that was
 *  a harmless double-`start`; for Sequencer and Macro it was a real defect,
 *  because `arm` is itself a toggle tracked OPBridge-side — the pedal armed
 *  on press and disarmed on release, netting nothing. Note bindings were
 *  never affected (`noteOn` is an explicit edge).
 *
 *  Expressed as `NOT (value == 0)` rather than by matching the press byte
 *  exactly, deliberately. Hardware disagrees on the press value (127 is
 *  common, 64 and others exist), so an exact match would bake one pedal's
 *  convention into a stored binding and break silently on a swap. Negation
 *  needs nothing new on either side: `Condition::Not { child }` already
 *  exists in `opbridge-rules::rule`, and `RuleConditionGroup`'s `'not'` +
 *  `child` already exist in `httpClient.ts`. No schema change, no stored
 *  press value, and no migration — existing bindings are corrected the next
 *  time this debounced sync runs.
 *
 *  Known limit, accepted: a CONTINUOUS CC (an expression pedal sweeping
 *  1..127) still matches on every non-zero value. That is unchanged from
 *  the previous behaviour, and this binding surface is footswitch-shaped —
 *  a continuous controller wants a different item kind, not a trigger.
 *
 *  `match.channel` is the RAW 0-based nibble decoded from the captured
 *  message's own status byte at Learn time (`midiTrigger/learn.ts`, reading
 *  OPBridge's traffic monitor since 2026-08-27 — previously the `/midi/recent`
 *  channel field, same value either way), and `ChannelEquals` compares
 *  straight against the same raw nibble (`engine.rs`), so the two agree by
 *  construction and need no base conversion. Noted because layout ITEMS are
 *  famously inconsistent here (0-based buttons/faders/LFO vs 1-based Waves and
 *  Matrix); a captured channel is not one of those and must not be
 *  "normalized" to match them.
 *
 *  ⚠️ Bindings learned BEFORE 2026-08-27 may carry `channel: 0` regardless of
 *  the hardware's real channel — Learn re-derived it from an already-masked
 *  status byte. Such a rule matches only channel 1 and looks bound but silent
 *  on anything else. Re-learn to correct; there is no migration, because the
 *  original channel was never captured and cannot be recovered. */
/**
 * `edge: 'release'` added 2026-08-28, and it closes the gap DEC-187 shipped
 * with. Operator ask, after confirming Learn works: *"the midi note should have
 * midi off value, default to 0 so when user lets go of button, the note stops
 * playing."*
 *
 * Momentary previously degraded to trigger (ON+OFF as a blip on the press)
 * because this file "only ever emits a PRESS condition". That was true of the
 * code and NOT true of the engine: `opbridge-rules` has had a `NoteOff` leaf
 * all along, and `engine.rs` matches `NoteEquals` and `ChannelEquals` against
 * `Event::MidiNoteOff` as well as note-on. So the release edge was expressible
 * the whole time and needed no OPBridge ask - checked against their source
 * before building, not assumed.
 *
 * - **note release** -> `noteOff` + same note + same channel. A velocity-0
 *   note-on (MIDI's other note-off convention) is decoded by OPBridge's
 *   `note_edge` as a note-off, so both hardware conventions land here.
 * - **cc release** -> `ccEquals { number, value: 0 }`, the exact inverse of the
 *   press condition's `not(value == 0)`, so press and release partition the
 *   value space with no overlap and no gap.
 */
function conditionFor(b: MidiTriggerBinding, edge: 'press' | 'release' = 'press'): RuleCondition {
  const matchLeaf: RuleCondition[] = b.match.type === 'note'
    ? edge === 'press'
      ? [{ op: 'noteOn' }, { op: 'noteEquals', value: b.match.number }]
      : [{ op: 'noteOff' }, { op: 'noteEquals', value: b.match.number }]
    : edge === 'press'
      ? [
          { op: 'ccEquals', number: b.match.number },
          { op: 'not', child: { op: 'ccEquals', number: b.match.number, value: 0 } },
        ]
      : [{ op: 'ccEquals', number: b.match.number, value: 0 }];
  return {
    op: 'and',
    children: [
      { op: 'portEquals', name: b.port },
      ...matchLeaf,
      { op: 'channelEquals', value: b.match.channel },
    ],
  };
}

/**
 * Returns one rule for most bindings, and TWO for a momentary Button - press
 * fires the ON action, release fires the OFF action, so holding the pedal holds
 * the note and letting go stops it.
 *
 * Every other kind stays single-rule on the press edge: trigger fires ON+OFF as
 * a blip in one rule, toggle flips server-side state, and Sequencer/Macro/LFO
 * have no release semantics at all.
 */
function rulesFor(item: EngineItem, cfg: BridgeConfig): Rule[] {
  const actions = actionsFor(item, cfg);
  if (actions.length === 0) return [];
  const label = item.name?.trim() || `${item.kind} trigger (${item.id})`;

  const isMomentaryButton = item.kind === 'button' && buttonMode(item) === 'momentary';
  if (isMomentaryButton && actions.length === 2) {
    // `actionsFor` returns [onAction, offAction]; split them across the edges
    // rather than firing both on the press.
    return [
      {
        id: `midiTrigger-${item.id}`,
        priority: 0,
        condition: conditionFor(item.midiTrigger!, 'press'),
        actions: [actions[0]!],
        enabled: true,
        name: `${label} (press)`,
      },
      {
        id: `midiTrigger-${item.id}-release`,
        priority: 0,
        condition: conditionFor(item.midiTrigger!, 'release'),
        actions: [actions[1]!],
        enabled: true,
        name: `${label} (release)`,
      },
    ];
  }

  return [{
    id: `midiTrigger-${item.id}`,
    priority: 0,
    condition: conditionFor(item.midiTrigger!, 'press'),
    actions,
    enabled: true,
    name: label,
  }];
}

/** The Panic MIDI trigger — DEC-154's one binding with no canvas item
 *  (`layout/projectStore.ts`'s `Project.panicMidiTrigger`). ONE action,
 *  OPBridge's own `panic.fire` (S282, released in `v1.15.135+b3949d0`),
 *  which does server-side what the header PANIC button does against that
 *  bridge: all-notes-off on EVERY output port (`MidiService::
 *  panic_all_outputs`, the same call as `POST /midi/panic`) plus stop every
 *  LFO, sequencer, macro AND timeline for this consumer. Read at their source
 *  (`rules_wiring.rs::dispatch_panic_action`, `arm.rs::KNOWN_MODULES`)
 *  before switching, not taken from the release note.
 *
 *  Replaced 2026-09-23 a 48-action `midi.sendRaw` storm to the ONE
 *  `defaultMidiOutput` with no engine stop, which is what a client-authored
 *  rule could express before the primitive existed (the operator's
 *  2026-08-13 "ship it now, file an ask for the gap"). `args` is `{}`: the
 *  port list and the engine registry both live server-side, so there is
 *  nothing for this client to resolve - and therefore no `cfg` dependency
 *  and no refusal branch.
 *
 *  ⚠️ Still narrower than the header button in ONE way: this rule arms on
 *  the PRIMARY bridge only (DEC-063), so it silences that machine's outputs
 *  and engines. The header button fans out to every connected bridge
 *  (`performance/panic.ts`). A pedal is wired into one machine, so that is
 *  the machine its rule can see. */
function panicRule(binding: MidiTriggerBinding): Rule {
  return {
    id: `midiTrigger-${PANIC_TRIGGER_ID}`,
    priority: 0,
    condition: conditionFor(binding),
    // `panic.fire` silences THIS machine at once, tab open or not; the `pwa`
    // action asks every open tab to run the full panic on every bridge
    // (2026-11-03 audit A-7, see performance/panic.ts PANIC_PWA_COMMAND).
    actions: [
      { module: 'panic', command: 'fire', args: {} },
      { module: 'pwa', command: PANIC_PWA_COMMAND, args: {} },
    ],
    enabled: true,
    name: 'Panic (all notes off + stop engines)',
  };
}

/** Monotonic per push, as OPBridge's `RuleSet.version` requires ("monotonic
 *  per (consumer, user); used for hot-reload version detection"). Seeded from
 *  the wall clock in seconds rather than starting at 1, so a page reload does
 *  not restart the sequence below a version the daemon has already armed. */
let ruleSetVersion = Math.floor(Date.now() / 1000);

/** OP-Motion's capture answer (2026-10-24, OPM4LBuilder's ask): every `/OP-Motion/state` reaching OPBridge's OSC
 *  input comes to this app as a `pwa_action` carrying the message, `{address, args}` (a lone placeholder keeps its
 *  type, so `args` is the array, `opbridge-rules/src/fill.rs`), for `timeline/motionCapture.ts` to store. The reply
 *  goes to the OPBridge on the computer running Live, which is the primary here (DEC-309's model, the same machine
 *  every OP-Motion message is sent through); pushed only while a control on the project captures or lands. */
export function motionStateRule(): Rule {
  return {
    id: 'opmotion-state',
    priority: 0,
    condition: { op: 'oscAddressEquals', address: MOTION_STATE_ADDRESS },
    actions: [{ module: 'pwa', command: MOTION_STATE_COMMAND, args: { address: '{address}', args: '{args}' } }],
    enabled: true,
    name: 'OP-Motion capture answer (to this app)',
  };
}

function buildRuleSet(cfg: BridgeConfig): RuleSet {
  const rules = boundEngineItems().flatMap((item) => rulesFor(item, cfg));
  const panicBinding = activeProject()?.panicMidiTrigger;
  if (panicBinding) rules.push(panicRule(panicBinding));
  if (projectUsesMotionPresets()) rules.push(motionStateRule());
  // `userId` deliberately omitted - see `RuleSet`. OPBridge adopts the
  // token's own user, which is the only correct value, and sending one can
  // only ever 403.
  return { consumerId: CONSUMER, version: ++ruleSetVersion, rules };
}

/** Kill switch. When this localStorage key is '1', rule sync is inert.
 *
 *  WHY IT EXISTS (measured 2026-09-22). The push is a WHOLE-SET REPLACE of
 *  the rules OPBridge arms for this user, built from the ACTIVE project
 *  only. A smoke boots with cleared localStorage and a throwaway project, so
 *  on connect it replaced the operator's real rule set with an empty one:
 *  one run of `project_sync_merges_across_devices` took the dev PC from 2
 *  armed Midi Fighter Twister bindings to 0. Same shape as projectSync's
 *  switch - an automated context cannot be told apart from a real device,
 *  so the harness declares which it is (`BotClient`'s `ruleSync` option). */
const DISABLE_KEY = 'opbuttons.ruleSync.disabled';

function syncDisabled(): boolean {
  try { return localStorage.getItem(DISABLE_KEY) === '1'; } catch { return false; }
}

async function syncMidiTriggerRules(): Promise<void> {
  if (syncDisabled()) return;
  const cfg = pickPrimaryConnected();
  if (!cfg) return;
  const conn = getConnection(cfg.id);
  if (!conn || conn.status() !== 'connected') return;
  const client = conn.httpClient();

  const boundItems = boundEngineItems();

  // Keep every bound item's own library entry current on OPBridge FIRST —
  // the rule referencing it is only useful if the id already resolves.
  // Parallel across items (2026-08-23 latency pass): each entry targets its
  // own sequencer/macro id independently, with no cross-item ordering
  // dependency - only "all of them land before the rule-set POST below"
  // matters, which Promise.all still guarantees. A project with N bound
  // items was previously paying N sequential round-trips here.
  await Promise.all(boundItems.map((item) => pushLibraryEntry(item)));

  const ruleSet = buildRuleSet(cfg);
  // Fingerprint EXCLUDES `version`, which changes on every build by design -
  // including it would make every debounce tick look like a real edit and
  // re-POST the identical rule set forever.
  const json = JSON.stringify({ ...ruleSet, version: 0 });
  if (json === lastPushedJson) return; // no-op edit (e.g. an unrelated project field changed)
  try {
    await client.putRuleSet(CONSUMER, ruleSet);
    lastPushedJson = json;
  } catch (err) {
    // Still never throws to the caller (every reactive-push module here
    // shares that contract) - but NO LONGER SILENT, and that change is the
    // whole reason this bug survived three weeks.
    //
    // This used to be a bare `catch {}` justified as "bridge hiccup,
    // unshipped route, transient blip". Those are all real, but the same
    // swallow also hid a PERMANENT 422: the rule set was missing required
    // `userId`/`version` fields, so not one rule ever armed while the
    // Inspector cheerfully badged every binding "A - fires even with the tab
    // closed". A retry-forever loop that cannot succeed and cannot complain
    // is indistinguishable from a working feature.
    //
    // `lastPushedJson` is deliberately NOT updated on failure, so the next
    // edit or reconnect retries rather than treating a rejected push as done.
    console.warn('[midiTrigger] rule-set push failed - bindings are NOT armed', err);
  }
}

function scheduleSync(): void {
  if (timer !== null) clearTimeout(timer);
  timer = window.setTimeout(() => {
    timer = null;
    void syncMidiTriggerRules();
  }, DEBOUNCE_MS);
}

/** Wire the reactive effects. Call once at app boot, alongside the other
 *  install*Sync() functions. */
export function installMidiTriggerRuleSync(): void {
  createEffect(on(projects, () => scheduleSync(), { defer: true }));
  createEffect(on(() => pickPrimaryConnected(), (cfg) => { if (cfg) void syncMidiTriggerRules(); }));
}

/** Diagnostic for smokes: the last-pushed RuleSet JSON, or null if nothing
 *  has been pushed yet this session. */
export function lastPushedRuleSetJson(): string | null {
  return lastPushedJson;
}
